/**
 * Edits group memberships independently because expiry and removal belong to a
 * membership rather than to the user's business-unit identity.
 */
import React, { useEffect, useState } from 'react';
import {
  useFieldArray,
  useForm,
  useWatch,
  type Control,
  type FieldValues,
} from 'react-hook-form';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { AlertType } from 'src/components/common/alerts/alert.types';
import { useToasterContext } from 'src/components/common/alerts/ToasterContext';
import { DeleteConfirmModal } from 'src/components/common/DeleteConfirmModal';
import type {
  BusinessUnitMemberDto,
  BusinessUnitMemberGroupDto,
  UpdateMembershipDto,
} from 'src/store/generated/leela-api-generated';
import {
  useBusinessUnitAdminControllerRevokeMembershipMutation,
  useBusinessUnitAdminControllerUpdateMembershipMutation,
} from 'src/store/leela-api';
import { getErrorsByField } from 'src/utils';
import { isExternalGroupRole } from 'src/utils/groupRoles';
import { getUserTypePresentation } from 'src/utils/membershipPresentation';
import {
  EditUserMembershipRow,
  type ExpiryAction,
} from './EditUserMembershipRow';

interface EditUserModalProps {
  member: BusinessUnitMemberDto;
  open: boolean;
  onClose: () => void;
}

interface MembershipFormValue {
  membershipId: string;
  expiryAction: ExpiryAction;
  expiresAt: string;
}

type ChangedMembershipFormValue = MembershipFormValue & {
  expiryAction: Exclude<ExpiryAction, 'UNCHANGED'>;
};

interface EditUserFormData {
  memberships: MembershipFormValue[];
}

interface MembershipUpdateFailure {
  groupName: string;
  message?: string;
}

const createDefaultValues = (
  member: BusinessUnitMemberDto,
): EditUserFormData => ({
  memberships: member.groups.map((group) => ({
    membershipId: group.membershipId,
    expiryAction: 'UNCHANGED',
    expiresAt: group.expiresAt?.slice(0, 10) ?? '',
  })),
});

const hasExpiryChange = (
  value: MembershipFormValue,
): value is ChangedMembershipFormValue => value.expiryAction !== 'UNCHANGED';

const createUpdateDto = (
  value: ChangedMembershipFormValue,
): UpdateMembershipDto =>
  value.expiryAction === 'CLEAR'
    ? { expiresAt: null }
    : { expiresAt: value.expiresAt };

const getApiErrorMessage = (error: unknown): string | undefined => {
  const { data } = (error ?? {}) as { data?: { message?: unknown } };
  const message = data?.message;

  if (typeof message === 'string') return message;
  if (!Array.isArray(message)) return undefined;

  const fieldMessages = Object.values(
    getErrorsByField(message as Array<Record<string, unknown>>),
  );
  return fieldMessages.length > 0 ? fieldMessages.join(' ') : undefined;
};

export const EditUserModal: React.FC<EditUserModalProps> = ({
  member,
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const { createToast } = useToasterContext();
  const [membershipToDelete, setMembershipToDelete] =
    useState<BusinessUnitMemberGroupDto>();
  const [updateMembership, { isLoading: isUpdating }] =
    useBusinessUnitAdminControllerUpdateMembershipMutation();
  const [revokeMembership, { isLoading: isDeleting }] =
    useBusinessUnitAdminControllerRevokeMembershipMutation();
  const { clearErrors, control, handleSubmit, reset, setError, setValue } =
    useForm<EditUserFormData>({
      defaultValues: createDefaultValues(member),
    });
  const { fields } = useFieldArray({
    control,
    name: 'memberships',
    rules: { required: true },
  });
  const memberships = useWatch({ control, name: 'memberships' }) ?? [];
  const formControl = control as unknown as Control<FieldValues>;

  useEffect(() => {
    reset(createDefaultValues(member));
    // The current user's membership edits must survive query refetches with equivalent new object references.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member.userId, open]);

  const expiryActionOptions = [
    { label: t('edit_user_expiry_unchanged'), value: 'UNCHANGED' },
    { label: t('edit_user_expiry_clear'), value: 'CLEAR' },
    { label: t('edit_user_expiry_set'), value: 'SET' },
  ];
  const userTypePresentation = getUserTypePresentation(member.userType);

  const handleSave = async (data: EditUserFormData): Promise<void> => {
    clearErrors('memberships');
    let hasInvalidExternalMembership = false;
    data.memberships.forEach((value, index) => {
      const group = member.groups.find(
        (candidate) => candidate.membershipId === value.membershipId,
      );
      if (
        !group ||
        !isExternalGroupRole(group.role) ||
        value.expiryAction !== 'CLEAR'
      ) { return; }

      hasInvalidExternalMembership = true;
      setError(`memberships.${index}.expiryAction`, {
        message: t('edit_user_external_expiry_required'),
        type: 'validate',
      });
    });
    if (hasInvalidExternalMembership) return;

    const changedMemberships = data.memberships.filter(hasExpiryChange);
    if (changedMemberships.length === 0) {
      onClose();
      return;
    }

    const failedUpdates = (
      await Promise.all(
        changedMemberships.map(
          async (value): Promise<MembershipUpdateFailure[]> => {
            try {
              await updateMembership({
                id: value.membershipId,
                updateMembershipDto: createUpdateDto(value),
              }).unwrap();
              const membershipIndex = data.memberships.findIndex(
                ({ membershipId }) => membershipId === value.membershipId,
              );
              if (membershipIndex >= 0) {
                setValue(
                  `memberships.${membershipIndex}.expiryAction`,
                  'UNCHANGED',
                );
              }
              return [];
            } catch (error: unknown) {
              return [
                {
                  groupName:
                    member.groups.find(
                      (group) => group.membershipId === value.membershipId,
                    )?.groupName ?? value.membershipId,
                  message: getApiErrorMessage(error),
                },
              ];
            }
          },
        ),
      )
    ).flatMap((failures) => failures);

    if (failedUpdates.length > 0) {
      const failureDetails = failedUpdates.map(({ groupName, message }) =>
        message ? `${groupName}: ${message}` : groupName,
      );
      const successMessage =
        failedUpdates.length < changedMemberships.length
          ? `${t('edit_user_update_success')}. `
          : '';
      createToast({
        message: `${successMessage}${t(
          'edit_user_update_error',
        )}: ${failureDetails.join(', ')}`,
        type: AlertType.ERROR,
      });
      return;
    }

    createToast({
      message: t('edit_user_update_success'),
      type: AlertType.SUCCESS,
    });
    onClose();
  };

  const handleDelete = async (): Promise<void> => {
    if (!membershipToDelete) return;

    try {
      await revokeMembership({ id: membershipToDelete.membershipId }).unwrap();
      createToast({
        message: t('edit_user_delete_success'),
        type: AlertType.SUCCESS,
      });
      setMembershipToDelete(undefined);
      onClose();
    } catch (error: unknown) {
      createToast({
        message: getApiErrorMessage(error) ?? t('edit_user_delete_error'),
        type: AlertType.ERROR,
      });
    }
  };

  return (
    <>
      <Modal
        data-testid="edit-user-modal"
        open={open}
        scrollContainer="modal"
        title={t('edit_user_title')}
        width={640}
        onClose={onClose}
      >
        <form onSubmit={handleSubmit(handleSave)}>
          <Column spacing="400">
            <Row spacing="200">
              <Text type="b300">{member.userId}</Text>
              <Tag type={userTypePresentation.tagType}>
                {t(userTypePresentation.labelKey)}
              </Tag>
            </Row>

            {fields.map((field, index) => {
              const group = member.groups.find(
                (candidate) => candidate.membershipId === field.membershipId,
              );
              if (!group) return null;

              return (
                <EditUserMembershipRow
                  control={formControl}
                  expiryAction={
                    memberships[index]?.expiryAction ?? 'UNCHANGED'
                  }
                  expiryActionOptions={expiryActionOptions}
                  group={group}
                  index={index}
                  key={field.id}
                  onDelete={setMembershipToDelete}
                />
              );
            })}
          </Column>

          <div style={{ marginTop: '24px' }}>
            <ModalFooter>
              <Row alignmentHorizontal="end" spacing="300">
                <Button
                  data-testid="edit-user-cancel"
                  type="secondary"
                  onClick={onClose}
                >
                  {t('edit_user_cancel')}
                </Button>
                <Button
                  data-testid="edit-user-save"
                  disabled={isUpdating || isDeleting}
                  submit
                  type="primary"
                >
                  {t('edit_user_save')}
                </Button>
              </Row>
            </ModalFooter>
          </div>
        </form>
      </Modal>

      <DeleteConfirmModal
        isDeleting={isDeleting}
        message={t('edit_user_delete_message')}
        open={!!membershipToDelete}
        title={t('edit_user_delete_title')}
        onCancel={() => {
          setMembershipToDelete(undefined);
        }}
        onConfirm={() => {
          void handleDelete();
        }}
      />
    </>
  );
};
